import { Business, Contact, Household, Person, Trust } from "@/schema"
import { Mention } from "@/schema/cross_reference"

import { MentionRecord } from "@/components/lexical/plugins/mention-dropdown"

export interface MentionUpdateData {
  household?: Household
  contact?: Contact
  business?: Business
  trust?: Trust
  person?: Person
  user_mention?: { id: string; name: string; taskInputField: "title" | "description" }
  removed_household?: Household
  removed_contact?: Contact
  removed_business?: Business
  removed_trust?: Trust
  removed_person?: Person
  removed_user_mention?: { id: string; name: string }
  add_mention?: Mention
  remove_mention?: Mention
}

interface UseTaskMentionsProps {
  onMentionUpdate?: (updates: MentionUpdateData) => void
  hasExistingRelationship?: boolean
}

export function useTaskMentions({ onMentionUpdate, hasExistingRelationship = false }: UseTaskMentionsProps = {}) {
  const handleMentionAdded = (mention: MentionRecord, taskInputField: "title" | "description" = "description") => {
    if (!onMentionUpdate) return

    switch (mention.recordType) {
      case "User":
        onMentionUpdate({
          user_mention: {
            id: mention.id,
            name: mention.label,
            taskInputField,
          },
        })
        break

      case "Person":
        onMentionUpdate({
          person: {
            id: mention.id,
            full_name: mention.label,
            mentionable_record: mention.mentionableRecord,
          } as Person,
          add_mention: {
            target_id: mention.id,
            target_type: "Person",
            target_label: mention.label,
          },
        })
        break

      case "Household": {
        if (hasExistingRelationship) {
          onMentionUpdate({
            add_mention: {
              target_id: mention.id,
              target_type: "Household",
              target_label: mention.label,
            },
          })
        } else {
          onMentionUpdate({
            household: {
              id: mention.id,
              name: mention.label,
            } as Household,
          })
        }
        break
      }
      case "Contact":
        if (hasExistingRelationship) {
          onMentionUpdate({
            add_mention: {
              target_id: mention.id,
              target_type: "Contact",
              target_label: mention.label,
            },
          })
        } else {
          onMentionUpdate({
            contact: {
              id: mention.id,
              person: {
                full_name: mention.label,
              },
            } as Contact,
          })
        }
        break
      case "Business":
        if (hasExistingRelationship) {
          onMentionUpdate({
            add_mention: {
              target_id: mention.id,
              target_type: "Business",
              target_label: mention.label,
            },
          })
        } else {
          onMentionUpdate({
            business: {
              id: mention.id,
              name: mention.label,
            } as Business,
          })
        }
        break
      case "Trust":
        if (hasExistingRelationship) {
          onMentionUpdate({
            add_mention: {
              target_id: mention.id,
              target_type: "Trust",
              target_label: mention.label,
            },
          })
        } else {
          onMentionUpdate({
            trust: {
              id: mention.id,
              name: mention.label,
              mentionable_record: mention.mentionableRecord,
            } as Trust,
            add_mention: {
              target_id: mention.id,
              target_type: "Trust",
              target_label: mention.label,
            },
          })
        }
        break
    }
  }

  const handleMentionRemoved = (mention: MentionRecord) => {
    if (!onMentionUpdate) return

    switch (mention.recordType) {
      case "User":
        onMentionUpdate({
          removed_user_mention: {
            id: mention.id,
            name: mention.label,
          },
        })
        break

      case "Person":
        onMentionUpdate({
          removed_person: {
            id: mention.id,
            full_name: mention.label,
            mentionable_record: mention.mentionableRecord,
          } as Person,
          remove_mention: {
            target_id: mention.id,
            target_type: "Person",
            target_label: mention.label,
          },
        })
        break

      case "Household": {
        onMentionUpdate({
          removed_household: {
            id: mention.id,
            name: mention.label,
          } as Household,
          remove_mention: {
            target_id: mention.id,
            target_type: "Household",
            target_label: mention.label,
          },
        })
        break
      }
      case "Contact":
        onMentionUpdate({
          removed_contact: {
            id: mention.id,
            person: {
              full_name: mention.label,
            },
          } as Contact,
          remove_mention: {
            target_id: mention.id,
            target_type: "Contact",
            target_label: mention.label,
          },
        })
        break
      case "Business":
        onMentionUpdate({
          removed_business: {
            id: mention.id,
            name: mention.label,
          } as Business,
          remove_mention: {
            target_id: mention.id,
            target_type: "Business",
            target_label: mention.label,
          },
        })
        break
      case "Trust":
        onMentionUpdate({
          removed_trust: {
            id: mention.id,
            name: mention.label,
            mentionable_record: mention.mentionableRecord,
          } as Trust,
          remove_mention: {
            target_id: mention.id,
            target_type: "Trust",
            target_label: mention.label,
          },
        })
        break
    }
  }

  return {
    handleMentionAdded,
    handleMentionRemoved,
  }
}
